import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';

type Scope = 'all' | 'course' | 'team' | 'device' | 'role';

interface Entitlement {
  scope: Scope;
  value: string;
}

interface Release {
  version: string;
  sha256: string;
  size_bytes: number;
  published_by?: string | null;
  withdrawn: boolean;
  stored_in?: 'blob' | 'database';
  created_at?: string | null;
  host_apis: string[];
}

interface MonitorModule {
  id: string;
  name: string;
  description: string;
  required: boolean;
  enabled: boolean;
  latest_version: string | null;
  kind?: 'web' | 'apk' | 'native';
  entitlements: Entitlement[];
  releases: Release[];
}

const SCOPE_LABEL: Record<Scope, string> = {
  all: 'Everyone',
  course: 'Course',
  team: 'Team (mission ID)',
  device: 'Device (phone app ID)',
  role: 'Role (entity type)',
};

const baseUrl = process.env.REACT_APP_API_URL || '/api';

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${baseUrl}/admin/monitor-modules${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${localStorage.getItem('access_token') || ''}`,
      ...(init.body && !(init.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const detail = (data as { detail?: unknown }).detail;
    throw new Error(typeof detail === 'string' ? detail : `HTTP ${response.status}`);
  }
  return data as T;
}

const entitlementLabel = (e: Entitlement) => (e.scope === 'all' ? 'Everyone' : `${e.scope}: ${e.value}`);

/**
 * Admin: publish signed Pathfinder Monitor modules (web bundles and APK modules) and choose which phones get them.
 * Bundles are packed and signed with pathfinder-monitor tools/sign-module.
 */
const MonitorModulesAdminPanel: React.FC = () => {
  const [modules, setModules] = useState<MonitorModule[]>([]);
  const [signingConfigured, setSigningConfigured] = useState(true);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [bundle, setBundle] = useState<File | null>(null);
  const [signature, setSignature] = useState<File | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [editing, setEditing] = useState<MonitorModule | null>(null);
  const [draft, setDraft] = useState<Entitlement[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api<{ signing_configured: boolean; modules: MonitorModule[] }>('');
      setModules(data.modules);
      setSigningConfigured(data.signing_configured);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load modules');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (action: () => Promise<unknown>, message: string) => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      await action();
      setSuccess(message);
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    if (!bundle || !signature) return;
    const form = new FormData();
    form.append('bundle', bundle);
    form.append('signature', (await signature.text()).trim());
    await run(async () => {
      const r = await api<{ id: string; version: string }>('/releases', { method: 'POST', body: form });
      setBundle(null);
      setSignature(null);
      return r;
    }, `Published ${bundle.name}`);
  };

  const setFlag = (m: MonitorModule, flag: 'required' | 'enabled', value: boolean) =>
    run(
      () => api(`/${m.id}`, { method: 'PATCH', body: JSON.stringify({ [flag]: value }) }),
      `${m.name}: ${flag} ${value ? 'on' : 'off'}`
    );

  const setWithdrawn = (m: MonitorModule, r: Release, withdrawn: boolean) =>
    run(
      () => api(`/${m.id}/releases/${encodeURIComponent(r.version)}`, { method: 'PATCH', body: JSON.stringify({ withdrawn }) }),
      `${m.name} ${r.version} ${withdrawn ? 'withdrawn' : 'restored'}`
    );

  const remove = (m: MonitorModule) => {
    if (!window.confirm(`Delete ${m.name} and all of its releases? Phones uninstall it on their next catalog refresh.`)) return;
    void run(() => api(`/${m.id}`, { method: 'DELETE' }), `Deleted ${m.name}`);
  };

  const saveEntitlements = () => {
    if (!editing) return;
    const m = editing;
    setEditing(null);
    void run(
      () => api(`/${m.id}/entitlements`, { method: 'PUT', body: JSON.stringify({ entitlements: draft }) }),
      `${m.name}: access updated`
    );
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Monitor Modules
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Web modules for the Pathfinder Monitor phone app. A module reaches a phone only when it has a live release and
        an access rule that covers the phone; required modules install themselves.
      </Typography>

      {!signingConfigured && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          MONITOR_MODULE_PUBLIC_KEYS is not set on this server, so no bundle can be published.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>
            Publish a release
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <Button variant="outlined" component="label" size="small">
              {bundle ? bundle.name : 'Bundle (.zip)'}
              <input hidden type="file" accept=".zip" onChange={(e) => setBundle(e.target.files?.[0] ?? null)} />
            </Button>
            <Button variant="outlined" component="label" size="small">
              {signature ? signature.name : 'Signature (.sig)'}
              <input hidden type="file" accept=".sig" onChange={(e) => setSignature(e.target.files?.[0] ?? null)} />
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<UploadIcon />}
              disabled={!bundle || !signature || busy || !signingConfigured}
              onClick={() => void publish()}
            >
              Publish
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle2">Modules</Typography>
        <IconButton size="small" onClick={() => void load()} disabled={loading}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Stack>

      {loading ? (
        <CircularProgress size={24} />
      ) : modules.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No modules published yet.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Module</TableCell>
              <TableCell>Live version</TableCell>
              <TableCell>Access</TableCell>
              <TableCell align="center">Required</TableCell>
              <TableCell align="center">Enabled</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {modules.map((m) => (
              <React.Fragment key={m.id}>
                <TableRow>
                  <TableCell padding="checkbox">
                    <IconButton size="small" onClick={() => setExpanded(expanded === m.id ? null : m.id)}>
                      {expanded === m.id ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {m.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {m.id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {m.latest_version ?? <Chip size="small" label="none live" />}
                    {m.kind === 'apk' && <Chip size="small" variant="outlined" label="APK" sx={{ ml: 1 }} />}
                    {m.kind === 'native' && <Chip size="small" variant="outlined" label="Built-in" sx={{ ml: 1 }} />}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
                      {m.entitlements.length === 0 ? (
                        <Chip size="small" color="warning" label="No one" />
                      ) : (
                        m.entitlements.map((e) => <Chip key={`${e.scope}:${e.value}`} size="small" label={entitlementLabel(e)} />)
                      )}
                      <Tooltip title="Edit access">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setDraft(m.entitlements.map((e) => ({ ...e })));
                            setEditing(m);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                  <TableCell align="center">
                    <Switch size="small" checked={m.required} disabled={busy} onChange={(e) => void setFlag(m, 'required', e.target.checked)} />
                  </TableCell>
                  <TableCell align="center">
                    <Switch size="small" checked={m.enabled} disabled={busy} onChange={(e) => void setFlag(m, 'enabled', e.target.checked)} />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Delete module">
                      <IconButton size="small" disabled={busy} onClick={() => remove(m)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={7} sx={{ py: 0, borderBottom: expanded === m.id ? undefined : 'none' }}>
                    <Collapse in={expanded === m.id} unmountOnExit>
                      <Box sx={{ py: 1.5 }}>
                        {m.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {m.description}
                          </Typography>
                        )}
                        {m.releases.map((r) => (
                          <Stack key={r.version} direction="row" spacing={2} alignItems="center" sx={{ py: 0.5 }}>
                            <Typography variant="body2" sx={{ minWidth: 70, textDecoration: r.withdrawn ? 'line-through' : 'none' }}>
                              {r.version}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                              {r.created_at ? new Date(r.created_at).toLocaleString() : ''} · {r.published_by ?? 'unknown'} ·{' '}
                              {(r.size_bytes / 1024).toFixed(0)} KB · sha256 {r.sha256.slice(0, 12)} · {r.host_apis.join(', ') || 'no host APIs'}
                              {r.stored_in === 'database' ? ' · in database (moves to Blob on next download)' : ''}
                            </Typography>
                            <FormControlLabel
                              control={
                                <Switch size="small" checked={!r.withdrawn} disabled={busy} onChange={(e) => void setWithdrawn(m, r, !e.target.checked)} />
                              }
                              label={<Typography variant="caption">{r.withdrawn ? 'Withdrawn' : 'Live'}</Typography>}
                            />
                          </Stack>
                        ))}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={editing !== null} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Who gets {editing?.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            A phone gets the module when any rule matches its Device Reference entry.
          </Typography>
          <Stack spacing={1.5}>
            {draft.map((e, i) => (
              <Stack key={i} direction="row" spacing={1} alignItems="center">
                <Select
                  size="small"
                  value={e.scope}
                  sx={{ minWidth: 200 }}
                  onChange={(ev) => setDraft(draft.map((d, j) => (j === i ? { scope: ev.target.value as Scope, value: d.value } : d)))}
                >
                  {(Object.keys(SCOPE_LABEL) as Scope[]).map((s) => (
                    <MenuItem key={s} value={s}>
                      {SCOPE_LABEL[s]}
                    </MenuItem>
                  ))}
                </Select>
                <TextField
                  size="small"
                  fullWidth
                  disabled={e.scope === 'all'}
                  placeholder={e.scope === 'all' ? 'Every registered phone' : 'Value'}
                  value={e.scope === 'all' ? '' : e.value}
                  onChange={(ev) => setDraft(draft.map((d, j) => (j === i ? { ...d, value: ev.target.value } : d)))}
                />
                <IconButton size="small" onClick={() => setDraft(draft.filter((_, j) => j !== i))}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
            <Box>
              <Button size="small" startIcon={<AddIcon />} onClick={() => setDraft([...draft, { scope: 'course', value: '' }])}>
                Add rule
              </Button>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={saveEntitlements}
            disabled={draft.some((e) => e.scope !== 'all' && !e.value.trim())}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MonitorModulesAdminPanel;
